import { useData } from 'vike-react/useData';
import type { Data } from './+data';
import { Container } from '../../../../components/layout/Container';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { CodeBlock } from '../../../../components/ui/code-block';
import { BreadcrumbListLd } from '../../../../components/seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../../../../components/seo/jsonld/FAQPage';
import { FicheCalculation } from '../../../../components/cee/FicheCalculation';
import { CeeCalculator } from '../../../../components/cee/CeeCalculator';
import type { CeeFicheCalculation } from '@shared/schemas/cee-fiche-calculation';
import {
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  HelpCircle,
  Zap,
} from 'lucide-react';
import { cn } from '../../../../lib/cn';
import { getSectorContext } from './sector-context';

const SECTOR_COLORS: Record<string, string> = {
  BAR: 'bg-brand-50 text-brand-700 border-brand-200',
  BAT: 'bg-lavender-50 text-lavender-700 border-lavender-200',
  IND: 'bg-amber-50 text-amber-700 border-amber-200',
  AGRI: 'bg-accent-50 text-accent-700 border-accent-200',
  TRA: 'bg-blue-50 text-blue-700 border-blue-200',
  RES: 'bg-stone-50 text-stone-600 border-stone-200',
};

// Map sector → URL d'estimation contextualisée.
function getEstimateHref(sector: string): string {
  switch (sector) {
    case 'BAR':
      return '/particuliers/calculer-mes-aides';
    case 'BAT':
      return '/tertiaire/diagnostic-cee';
    case 'IND':
      return '/industrie/diagnostic-cee';
    case 'AGRI':
      return '/agriculteurs/diagnostic-cee';
    case 'TRA':
    case 'RES':
    default:
      return '/diagnostic';
  }
}

function getEstimateLabel(sector: string): string {
  switch (sector) {
    case 'BAR':
      return 'Calculer mes aides';
    case 'BAT':
      return "Lancer l'audit tertiaire";
    case 'IND':
      return 'Diagnostic industriel';
    case 'AGRI':
      return 'Diagnostic agricole';
    default:
      return 'Estimer ma prime';
  }
}

export default function CeeFichePage() {
  const { fiche, related } = useData<Data>();
  const sectorColor = SECTOR_COLORS[fiche.sector] ?? SECTOR_COLORS.RES;
  const sectorContext = getSectorContext(fiche.sector);

  // Cast typé du JSONB en provenance de Drizzle.
  const calculation = (fiche.calculation ?? null) as CeeFicheCalculation | null;
  const hasCalculation =
    !!calculation &&
    (!!calculation.denomination ||
      !!calculation.formula ||
      (calculation.tables?.length ?? 0) > 0 ||
      !!calculation.example ||
      !!calculation.conversion);

  // Simulateur interactif disponible si la fiche ATEE a une calculatrice (incl. non calculable
  // qui affichera un fallback "demande de devis").
  const ateeCalculatrice = fiche.ateeCalculatrice ?? null;
  const hasSimulator = !!ateeCalculatrice;

  // TOC dynamique selon les sections présentes.
  const toc: Array<{ id: string; label: string }> = [
    { id: 'conditions-eligibilite', label: "Conditions d'éligibilité" },
    { id: 'profils-eligibles', label: 'Profils éligibles' },
  ];
  if (hasCalculation) {
    if (calculation?.denomination)
      toc.push({ id: 'comprendre-le-code', label: 'Comprendre le code' });
    if (calculation?.formula) toc.push({ id: 'formule-calcul', label: 'Formule de calcul' });
    if ((calculation?.tables?.length ?? 0) > 0)
      toc.push({ id: 'bareme', label: 'Barème officiel' });
    if (calculation?.example) toc.push({ id: 'exemple-calcul', label: 'Exemple chiffré' });
    if (calculation?.conversion) toc.push({ id: 'conversion-euros', label: 'Conversion en euros' });
  } else if (fiche.savingsKwh) {
    toc.push({ id: 'calcul-kwh-cumac', label: 'Calcul kWh cumac' });
  }
  if (hasSimulator) toc.push({ id: 'simulateur-cee', label: 'Simulateur de prime' });
  toc.push({ id: 'pieces-justificatives', label: 'Pièces justificatives' });
  toc.push({ id: 'questions-frequentes', label: 'Questions fréquentes' });

  const estimateHref = getEstimateHref(fiche.sector);
  const estimateLabel = getEstimateLabel(fiche.sector);

  return (
    <div className="min-h-screen bg-white">
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'CEE', url: 'https://solutionecofrance.fr/cee' },
          { name: 'Fiches', url: 'https://solutionecofrance.fr/cee/fiches' },
          { name: fiche.code, url: `https://solutionecofrance.fr/cee/fiches/${fiche.code}` },
        ]}
      />
      <FAQPageLd
        items={sectorContext.faq.map((f) => ({ question: f.question, answer: f.answer }))}
      />

      {/* ── Hero fiche — style Stripe Docs ── */}
      <div className="border-b border-stone-200/80 bg-stone-50 py-10 md:py-14">
        <Container className="max-w-4xl">
          <a
            href="/cee/fiches"
            className="inline-flex items-center gap-1.5 text-sm text-stone-500 transition-colors hover:text-brand-700"
          >
            <ChevronLeft className="h-4 w-4" />
            Catalogue des fiches
          </a>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span
              className={cn(
                'rounded-md border px-2.5 py-1 font-mono text-xs font-bold tracking-widest',
                sectorColor,
              )}
            >
              {fiche.code}
            </span>
            <span className={cn('rounded-md border px-2 py-0.5 text-xs font-medium', sectorColor)}>
              {fiche.sectorLabel}
            </span>
            {fiche.version && (
              <span className="text-xs text-stone-400">Version {fiche.version}</span>
            )}
            {fiche.arrete && (
              <span className="text-xs text-stone-400">Arrêté du {fiche.arrete}</span>
            )}
          </div>

          <h1 className="mt-4 text-balance font-display text-2xl font-bold tracking-tight text-ink md:text-3xl">
            {fiche.label}
          </h1>
          <p className="mt-3 max-w-2xl text-stone-600">{fiche.description}</p>

          <div className="mt-6 flex flex-wrap gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-brand-200 bg-brand-50/60 px-4 py-2">
              <Zap className="h-4 w-4 text-brand-600" />
              <span className="text-sm">
                <span className="font-medium text-ink">{fiche.primeEstimate}</span>
                <span className="ml-1 text-stone-500">prime estimée</span>
              </span>
            </div>
            {fiche.savingsKwh && (
              <div className="flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2">
                <span className="text-sm text-stone-600">
                  <span className="font-semibold text-ink">
                    {fiche.savingsKwh.toLocaleString('fr-FR')}
                  </span>{' '}
                  kWh cumac / {fiche.unit}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2">
              <span className="text-xs text-stone-400">{fiche.zone}</span>
            </div>
          </div>
        </Container>
      </div>

      {/* ── Corps — layout 2 colonnes ── */}
      <Container className="max-w-4xl py-10 md:py-14">
        <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
          {/* Contenu principal */}
          <div className="min-w-0 flex-1 space-y-12">
            {/* Bandeau audience contextuel selon le secteur */}
            <section
              className={cn(
                'rounded-2xl border bg-gradient-to-br p-5 md:p-6',
                fiche.sector === 'BAR'
                  ? 'border-brand-200 from-brand-50/70 to-paper'
                  : fiche.sector === 'BAT'
                    ? 'border-lavender-200 from-lavender-50/70 to-paper'
                    : fiche.sector === 'IND'
                      ? 'border-amber-200 from-amber-50/70 to-paper'
                      : fiche.sector === 'AGRI'
                        ? 'border-accent-200 from-accent-50/70 to-paper'
                        : 'border-stone-200 from-stone-50/70 to-paper',
              )}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                {sectorContext.audience.eyebrow}
              </p>
              <h2 className="mt-1 font-display text-[18px] font-bold leading-tight tracking-tight text-ink md:text-xl">
                {sectorContext.audience.title}
              </h2>
              <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-stone-600">
                {sectorContext.audience.description}
              </p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                <a
                  href={sectorContext.audience.primaryCta.href}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-3.5 py-2 text-[13px] font-semibold text-ivory-50 transition-all duration-150 ease-out hover:-translate-y-0.5 hover:bg-stone-800 hover:shadow-elevated"
                >
                  {sectorContext.audience.primaryCta.label}
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
                </a>
                {sectorContext.audience.secondaryCta && (
                  <a
                    href={sectorContext.audience.secondaryCta.href}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-paper px-3.5 py-2 text-[13px] font-semibold text-ink transition-colors hover:border-stone-300 hover:bg-stone-50"
                  >
                    {sectorContext.audience.secondaryCta.label}
                  </a>
                )}
              </div>
            </section>

            {/* Conditions d'éligibilité */}
            <section>
              <h2
                id="conditions-eligibilite"
                className="mb-4 font-display text-xl font-bold text-ink"
              >
                Conditions d'éligibilité
              </h2>
              <ul className="space-y-2">
                {(fiche.conditions as string[]).map((c, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                    {c}
                  </li>
                ))}
              </ul>
            </section>

            {/* Profils éligibles */}
            <section>
              <h2 id="profils-eligibles" className="mb-3 font-display text-xl font-bold text-ink">
                Profils éligibles
              </h2>
              <div className="flex flex-wrap gap-2">
                {fiche.eligibleProfiles.map((p) => (
                  <Badge key={p} variant="default">
                    {p}
                  </Badge>
                ))}
              </div>
            </section>

            {/* Bloc de calcul pédagogique (style Opera Énergie) */}
            {hasCalculation && calculation && (
              <FicheCalculation calculation={calculation} code={fiche.code} />
            )}

            {/* Simulateur interactif (Phase 4) — disponible pour toute fiche ATEE. */}
            {ateeCalculatrice && (
              <CeeCalculator ficheCode={fiche.code} calculatrice={ateeCalculatrice} />
            )}

            {/* Fallback : ancien CodeBlock kWh cumac (fiches non encore enrichies) */}
            {!hasCalculation && fiche.savingsKwh && (
              <section>
                <h2 id="calcul-kwh-cumac" className="mb-3 font-display text-xl font-bold text-ink">
                  Calcul kWh cumac indicatif
                </h2>
                <CodeBlock
                  label={`${fiche.code} — Formule simplifiée`}
                  code={`Économies = ${fiche.savingsKwh.toLocaleString('fr-FR')} kWh cumac / ${fiche.unit}\nZone       : ${fiche.zone}\nVersion    : ${fiche.version ?? 'N/A'}  |  Arrêté : ${fiche.arrete ?? 'N/A'}`}
                  showCopy
                />
                <p className="mt-2 text-xs text-stone-400">
                  Valeur réglementaire — le calcul précis dépend de la formule de la fiche
                  officielle.
                </p>
              </section>
            )}

            {/* Pièces justificatives — communes + secteur */}
            <section>
              <h2
                id="pieces-justificatives"
                className="mb-3 font-display text-xl font-bold text-ink"
              >
                Pièces justificatives typiques
              </h2>
              <ul className="space-y-2 text-sm text-stone-700">
                {[
                  'Devis signé avant le début des travaux',
                  "Facture de l'artisan certifié RGE mentionnant la fiche CEE",
                  "Attestation sur l'honneur du bénéficiaire",
                  ...sectorContext.extraDocs,
                ].map((doc, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-stone-400" />
                    {doc}
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-stone-400">
                Liste indicative — les pièces exactes dépendent de la fiche officielle, de votre
                profil et de l'administration en charge des CEE. Notre mandat couvre la constitution
                complète du dossier.
              </p>
            </section>

            {/* FAQ contextuelle par secteur */}
            <section>
              <h2
                id="questions-frequentes"
                className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink"
              >
                <HelpCircle className="h-5 w-5 text-brand-600" strokeWidth={1.8} />
                Questions fréquentes — secteur {fiche.sector}
              </h2>
              <div className="space-y-3">
                {sectorContext.faq.map((item, i) => (
                  <details
                    key={i}
                    className="group rounded-xl border border-stone-200 bg-paper p-4 open:bg-stone-50/60"
                  >
                    <summary className="cursor-pointer list-none">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-display text-[14.5px] font-semibold text-ink">
                          {item.question}
                        </p>
                        <ChevronRight
                          className="mt-1 h-4 w-4 shrink-0 text-stone-400 transition-transform group-open:rotate-90"
                          strokeWidth={2}
                        />
                      </div>
                    </summary>
                    <p className="mt-3 text-[13.5px] leading-relaxed text-stone-600">
                      {item.answer}
                    </p>
                  </details>
                ))}
              </div>
            </section>

            {/* Fiches connexes du même secteur */}
            {related.length > 0 && (
              <section className="border-t border-stone-200 pt-10">
                <div className="mb-5 flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                      Autres fiches {fiche.sector}
                    </p>
                    <h2 className="mt-1 font-display text-xl font-bold text-ink">
                      Opérations connexes
                    </h2>
                  </div>
                  <a
                    href={`/cee/fiches?secteur=${fiche.sector}`}
                    className="hidden items-center gap-1 text-[13px] font-semibold text-brand-700 hover:underline sm:inline-flex"
                  >
                    Toutes les fiches {fiche.sector}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </a>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {related.map((rf) => (
                    <a
                      key={rf.code}
                      href={`/cee/fiches/${rf.code}`}
                      className="group flex h-full flex-col gap-2 rounded-xl border border-stone-200 bg-paper p-4 transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
                    >
                      <span
                        className={cn(
                          'inline-flex w-fit items-center rounded-md border px-2 py-0.5 font-mono text-[10.5px] font-bold tracking-widest',
                          sectorColor,
                        )}
                      >
                        {rf.code}
                      </span>
                      <p className="line-clamp-2 font-display text-[14px] font-semibold leading-snug text-ink group-hover:text-brand-800">
                        {rf.label}
                      </p>
                      <p className="mt-auto text-[12px] text-stone-500">{rf.primeEstimate}</p>
                    </a>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar droite — TOC + CTA */}
          <aside className="lg:sticky lg:top-20 lg:h-fit lg:w-64 lg:shrink-0">
            <div className="rounded-2xl border border-stone-200 bg-stone-50 p-5 shadow-subtle">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">
                Sur cette page
              </p>
              <nav className="mt-3 space-y-1.5 text-sm text-stone-600">
                {toc.map(({ id, label }) => (
                  <a
                    key={id}
                    href={`#${id}`}
                    className="block rounded-md px-2 py-1 transition-colors hover:bg-stone-100 hover:text-brand-700"
                  >
                    {label}
                  </a>
                ))}
              </nav>

              <div className="mt-6 space-y-2">
                <Button asChild className="w-full">
                  <a href={estimateHref}>{estimateLabel}</a>
                </Button>
                <Button asChild variant="outline" size="sm" className="w-full">
                  <a href="/contact">Parler à un expert</a>
                </Button>
              </div>

              {/* Téléchargement des PDFs officiels locaux (Phase 5) */}
              {fiche.localPdfs?.ficheStandardisee && (
                <a
                  href={fiche.localPdfs.ficheStandardisee}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs font-medium text-stone-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                >
                  <Download className="h-3.5 w-3.5" />
                  Fiche standardisée (PDF)
                </a>
              )}
              {fiche.localPdfs?.ficheCalcul && (
                <a
                  href={fiche.localPdfs.ficheCalcul}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs font-medium text-stone-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                >
                  <Download className="h-3.5 w-3.5" />
                  Fiche de calcul (PDF)
                </a>
              )}

              {fiche.sourceUrl && !fiche.localPdfs?.ficheStandardisee && (
                <a
                  href={fiche.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 flex items-center gap-1.5 text-xs text-stone-400 hover:text-brand-700"
                >
                  <ExternalLink className="h-3 w-3" />
                  Source officielle (atee.fr)
                </a>
              )}
            </div>
          </aside>
        </div>
      </Container>
    </div>
  );
}
